import { useStoreMapSettings } from '@src/store/modules/mapSettings';
import type { CurrentCity } from '@src/api/city';
import type { MapLayerItem } from '@src/store/modules/mapSettings';

/** 地图缩放级别配置 */
export interface MapZoomLevel {
    maxZoom: number;
    minZoom: number;
    defaultZoom: number;
    areaMaxZoom: number;
}

/** initMap 返回值 */
export interface InitMapResult {
    map: TMap.Map;
    measureTool: TMap.tools.MeasureTool;
}

/** initMap 额外配置 */
export type InitMapOptions = Partial<ConstructorParameters<typeof TMap.Map>[1]>;

/** 底图图层克隆入参 */
export type CloneableMapLayer = MapLayerItem & { features?: string[] };

// ---------------------------- 地图缩放 ----------------------------

/** 地图缩放级别配置 */
export const MAP_ZOOM_LEVEL: MapZoomLevel = {
    maxZoom: 20,
    minZoom: 8,
    defaultZoom: 16,
    areaMaxZoom: 13.5, // 区域最大缩放级别
};

/** 图层缩放级别配置（min：闭区间，max：开区间） */
export const MAP_LAYER_ZOOM = {
    AREA: {
        minZoom: MAP_ZOOM_LEVEL.minZoom,
        maxZoom: MAP_ZOOM_LEVEL.areaMaxZoom,
    },
    STREET: {
        minZoom: MAP_ZOOM_LEVEL.areaMaxZoom,
        maxZoom: MAP_ZOOM_LEVEL.defaultZoom,
    },
    REGULATORY: {
        minZoom: MAP_ZOOM_LEVEL.defaultZoom,
        maxZoom: MAP_ZOOM_LEVEL.maxZoom,
    },
};

// ---------------------------- 底图样式（内部） ----------------------------

/** 矢量底图可选样式 ID 列表 */
export const VECTOR_MAP_STYLE_TYPES = ['style5', 'style4', 'style3', 'style1', 'style2', 'style6'] as const;

/** 默认矢量底图样式 */
export const DEFAULT_MAP_STYLE_ID = 'style1';

/**
 * 解析初始底图样式 ID。
 * 优先使用 store 中已激活的底图类型，无效时回退到默认样式。
 */
const resolveInitialMapStyleId = (activeBaseMapType: string | null | undefined): string => {
    if (activeBaseMapType && (VECTOR_MAP_STYLE_TYPES as readonly string[]).includes(activeBaseMapType)) {
        return activeBaseMapType;
    }
    return DEFAULT_MAP_STYLE_ID;
};

const cloneMapLayer = (layer: CloneableMapLayer): CloneableMapLayer => ({
    ...layer,
    ...(Array.isArray(layer.features) ? { features: [...layer.features] } : {}),
});

// ---------------------------- 腾讯地图 SDK ----------------------------

/** 动态加载腾讯地图 GL JS SDK（已加载则直接复用 window.TMap，避免重复注入 script） */
export function TMapLoader(key: string): Promise<TMapNamespace> {
    if (window.TMap) {
        return Promise.resolve(window.TMap);
    }

    return new Promise((resolve, reject) => {
        (window as Window & { init?: () => void }).init = function () {
            resolve(window.TMap);
        };
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://map.qq.com/api/gljs?v=1.exp&callback=init&key=${key}&libraries=view,geometry,model,tools,visualization,service`;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

// ---------------------------- 地图初始化 ----------------------------

/**
 * 创建腾讯地图实例并挂载到 DOM。
 * 底图图层（矢量 / 路况 / 卫星）从 mapSettings store 读取，缺失时写入默认值。
 */
export async function initMap(
    TMap: TMapNamespace,
    currentCity: CurrentCity,
    domId = 'mapContainer',
    option: InitMapOptions = {}
): Promise<InitMapResult> {
    const mapSettingsStore = useStoreMapSettings();

    return new Promise(resolve => {
        const mapContainer = document.getElementById(domId) as HTMLDivElement;
        // TMap.LatLng 入参为 (lat, lng)，currentCity.latlon 为 [lng, lat]
        const latlng: [number, number] =
            currentCity?.latlon?.length > 0 ? [currentCity.latlon[1], currentCity.latlon[0]] : [31.820567, 117.227267];
        const baseMapLayers: CloneableMapLayer[] = [];

        const { vectorLayer, trafficLayer, satelliteLayer, activeBaseMapType } = mapSettingsStore.mapSettings;

        // 矢量底图层
        if (vectorLayer) {
            baseMapLayers.push(cloneMapLayer(vectorLayer));
        } else {
            const defaultVectorLayer: CloneableMapLayer = {
                type: 'vector',
                features: ['base', 'building2d', 'label', 'building3d', 'point'],
            };
            baseMapLayers.push(defaultVectorLayer);
            mapSettingsStore.patchMapSettings({ vectorLayer: cloneMapLayer(defaultVectorLayer) });
        }

        if (trafficLayer) {
            // 路况图层：叠加后精简矢量层要素，避免与路况冲突
            baseMapLayers.push(cloneMapLayer(trafficLayer));
            baseMapLayers[0].features = ['base', 'label', 'point'];
        }

        if (satelliteLayer) {
            // 卫星图层
            baseMapLayers.push(cloneMapLayer(satelliteLayer));
        }

        mapSettingsStore.patchMapSettings({
            baseMapLayers: baseMapLayers.map(cloneMapLayer),
            vectorLayer: cloneMapLayer(baseMapLayers[0]),
        });

        const map = new TMap.Map(mapContainer, {
            center: new TMap.LatLng(latlng[0], latlng[1]),
            zoom: MAP_ZOOM_LEVEL.defaultZoom,
            maxZoom: MAP_ZOOM_LEVEL.maxZoom,
            mapStyleId: resolveInitialMapStyleId(activeBaseMapType),
            minZoom: MAP_ZOOM_LEVEL.minZoom,
            baseMap: baseMapLayers.map(cloneMapLayer) as InitMapOptions['baseMap'],
            ...option,
        });

        const mapControl = map.getControl(TMap.constants.DEFAULT_CONTROL_ID.ZOOM);
        mapControl.setPosition(TMap.constants.CONTROL_POSITION.BOTTOM_RIGHT);
        map.getControl(TMap.constants.DEFAULT_CONTROL_ID.ROTATION).setPosition(TMap.constants.CONTROL_POSITION.BOTTOM_RIGHT);

        const measureTool = new TMap.tools.MeasureTool({ map });
        resolve({ map, measureTool });
    });
}

// ---------------------------- 坐标工厂 ----------------------------

/**
 * 腾讯地图 LatLng 工厂（lite 不做 GCJ→WGS 转换）。
 * GeoJSON `[lng, lat]` 传入时写 `createNewTMapLatLng(lat, lng)`。
 */
export function createNewTMapLatLng(latitude: number, longitude: number, height?: number): TMap.LatLng {
    if (!window.TMap) console.warn('未加载腾讯地图');
    return height != null ? new window.TMap.LatLng(latitude, longitude, height) : new window.TMap.LatLng(latitude, longitude);
}
